/**
 * hlsTier.js — decode tier 1: MSE via hls.js (PLAYBACK-HANDOFF.md §8 point 1).
 *
 * Config proven against production; ported from mock FE `attachHls()`, with
 * one deliberate difference: `liveSyncDurationCount` is left at hls.js's
 * own default (3), not the mock's `1` — handoff: "liveSyncDurationCount: 1
 * is a sandbox instrument, not a production setting... It only matters for
 * a playlist that lacks #EXT-X-START", which every production playlist
 * carries (§4.5).
 *
 * ⚠ This module NEVER calls videoEl.play(). Starting playback is owned
 * exclusively by the autostart gate (handoff §7.2 trap: "If you have more
 * than one place that can call play(), you have this bug" — this exact bug
 * shipped once when attachHls() also called video.play() unconditionally).
 */
import Hls from 'hls.js';

/**
 * @param {object} args
 * @param {HTMLVideoElement} args.videoEl
 * @param {string} args.playlistUrl
 * @param {() => string} args.getToken   read live, never captured (§5)
 * @param {number} [args.expectedDurationSec] the segment's known full length
 *   (from playback_ack/cache row duration_ms) -- see onEndedShort below.
 * @param {object} args.callbacks
 *   onBufferedSeconds(totalDurationSec) — §7.4 clip-timeline source
 *   onFragBuffered()                    — first playable chunk -> markReady()
 *   onPlaying()                         — first actual rendered frame (display only)
 *   onFatalError(detail: string)        — fatal hls.js error; caller decides
 *     whether to fall through to tier 2/3 (§8: an init-segment codec reject
 *     must fall through, not surface as a playback error)
 *   onEndedShort()                      — the playlist carries #EXT-X-ENDLIST
 *     (hls.js will now NEVER reload it — confirmed in hls.js's own
 *     LevelController.playlistLoaded: `else { this.clearTimer(); }` once
 *     `details.live` is false) while its reported duration is still well
 *     short of expectedDurationSec. Measured 2026-09-12: vgps_storage seals
 *     a row 'partial' (which also carries ENDLIST) on a device pause/abort,
 *     but the SAME segment_uid can reopen and keep growing
 *     (ReopenSealedDeviceRecording) -- hls.js has no way to know that and
 *     treats ENDLIST as always-final VOD, per spec. Only fired once, and
 *     only before this tier has ever produced a playable frame -- the
 *     caller's job is to fall through to the JS-tier pump (which already
 *     polls tolerantly forever), not to hack around hls.js's own state.
 * @returns {{ destroy(): void }}
 */
export function createHlsTier({ videoEl, playlistUrl, getToken, expectedDurationSec, callbacks }) {
  const { onBufferedSeconds, onFragBuffered, onPlaying, onFatalError, onEndedShort } = callbacks || {};
  let endedShortReported = false;

  const hls = new Hls({
    manifestLoadingMaxRetry: 10,
    manifestLoadingRetryDelay: 1000,
    manifestLoadingMaxRetryTimeout: 10000,
    levelLoadingMaxRetry: 10,
    levelLoadingRetryDelay: 1000,
    xhrSetup: (xhr) => {
      const t = getToken();
      if (t) xhr.setRequestHeader('Authorization', 'Bearer ' + t);
    },
  });

  let sawFragBuffered = false;
  const reportDuration = (data) => {
    if (data.details && data.details.totalduration != null) {
      onBufferedSeconds && onBufferedSeconds(data.details.totalduration);
    }
    if (
      !endedShortReported && !sawFragBuffered && data.details &&
      data.details.live === false && expectedDurationSec > 0 &&
      data.details.totalduration < expectedDurationSec - 2
    ) {
      endedShortReported = true;
      onEndedShort && onEndedShort();
    }
  };
  hls.on(Hls.Events.LEVEL_LOADED, (evt, data) => reportDuration(data));
  hls.on(Hls.Events.LEVEL_UPDATED, (evt, data) => reportDuration(data));
  hls.on(Hls.Events.FRAG_BUFFERED, () => {
    if (!sawFragBuffered) {
      sawFragBuffered = true;
      onFragBuffered && onFragBuffered();
    }
  });
  hls.on(Hls.Events.ERROR, (evt, data) => {
    if (data.fatal) onFatalError && onFatalError(data.details);
  });

  let sawPlaying = false;
  const handlePlaying = () => {
    if (sawPlaying) return;
    sawPlaying = true;
    onPlaying && onPlaying();
  };
  videoEl.addEventListener('playing', handlePlaying);

  hls.loadSource(playlistUrl);
  hls.attachMedia(videoEl);
  // Muted always: playback is automatic (§7.1/§7.2), and an unmuted <video>
  // nobody clicked is exactly what the browser's autoplay policy blocks.
  videoEl.muted = true;

  return {
    destroy() {
      videoEl.removeEventListener('playing', handlePlaying);
      try { hls.destroy(); } catch (_) { /* ignore */ }
    },
  };
}
